/**
 * Platform Fee Config — admin: read current, update (audited), view history.
 * Backend: feat/bet-platform-fee (PR2).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './VouchersPage.css';

interface Config {
  basisPoints: number;
  minFeeCents: number;
  maxFeeCents: number | null;
  enabled: boolean;
  updatedBy: string;
  reason: string | null;
  updatedAt: string;
}

interface HistoryRow {
  id: number;
  basisPoints: number;
  minFeeCents: number;
  maxFeeCents: number | null;
  enabled: boolean;
  changedBy: string;
  changedAt: string;
  reason: string | null;
}

const PlatformFeeConfigPage: React.FC = () => {
  const [config, setConfig] = useState<Config | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, h] = await Promise.all([
        apiService.getPlatformFeeConfig(),
        apiService.getPlatformFeeConfigHistory({ limit: 50, offset: 0 }),
      ]);
      setConfig(c);
      setHistory(h.rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fee config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!loading && error && !config) {
    return (
      <div className="vouchers-page">
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={() => void load()} style={{ marginTop: '16px' }}>
          Retry
        </button>
      </div>
    );
  }

  if (loading || !config) {
    return <div className="vouchers-page"><div className="loading">Loading fee config…</div></div>;
  }

  const previewCents = (stakeCents: number): number => {
    if (!config.enabled || stakeCents <= 0) return 0;
    const raw = Math.floor((stakeCents * config.basisPoints) / 10_000);
    let fee = Math.max(raw, config.minFeeCents);
    if (config.maxFeeCents !== null) fee = Math.min(fee, config.maxFeeCents);
    return fee;
  };

  return (
    <div className="vouchers-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Platform fee config</h1>
          <div className="page-sub">Singleton row · changes are audited and broadcast to all API instances within 30 seconds</div>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing(true)}>Edit</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError(null)}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess(null)}>{success}</div>}

      <div className="card">
        <div className="card-body">
          <h2 className="section-h">Current</h2>
          <div className="config-grid">
            <div className="config-row">
              <div className="config-label">Status</div>
              <div className={`config-value ${config.enabled ? 'enabled' : 'disabled'}`}>
                {config.enabled ? '● Enabled' : '○ Disabled'}
              </div>
            </div>
            <div className="config-row">
              <div className="config-label">Rate</div>
              <div className="config-value">{(config.basisPoints / 100).toFixed(2)}% <span className="muted">({config.basisPoints} bps)</span></div>
            </div>
            <div className="config-row">
              <div className="config-label">Min fee</div>
              <div className="config-value">{(config.minFeeCents / 100).toFixed(2)} EUR</div>
            </div>
            <div className="config-row">
              <div className="config-label">Max fee</div>
              <div className="config-value">{config.maxFeeCents === null ? '— (no cap)' : `${(config.maxFeeCents / 100).toFixed(2)} EUR`}</div>
            </div>
            <div className="config-row">
              <div className="config-label">Last changed by</div>
              <div className="config-value">{config.updatedBy}</div>
            </div>
            <div className="config-row">
              <div className="config-label">Last changed at</div>
              <div className="config-value">{new Date(config.updatedAt).toLocaleString()}</div>
            </div>
            <div className="config-row config-row-wide">
              <div className="config-label">Last reason</div>
              <div className="config-value">{config.reason ?? '—'}</div>
            </div>
          </div>

          <h2 className="section-h">Preview at common stakes</h2>
          <table className="vouchers-table">
            <thead>
              <tr>
                <th>Stake (EUR)</th>
                <th>Stake (cents)</th>
                <th>Fee (cents)</th>
                <th>Fee (EUR)</th>
                <th>Effective rate</th>
              </tr>
            </thead>
            <tbody>
              {[100, 500, 1000, 2500, 10000, 50000].map((stakeCents) => {
                const fee = previewCents(stakeCents);
                const eff = stakeCents > 0 ? ((fee * 10000) / stakeCents).toFixed(0) : '0';
                return (
                  <tr key={stakeCents}>
                    <td>{(stakeCents / 100).toFixed(2)}</td>
                    <td>{stakeCents}</td>
                    <td>{fee}</td>
                    <td>{(fee / 100).toFixed(2)}</td>
                    <td>{eff} bps</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h2 className="section-h">Change history (last 50)</h2>
          {history.length === 0 ? (
            <div className="empty">No changes yet.</div>
          ) : (
            <table className="vouchers-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Rate</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>Enabled</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>{new Date(h.changedAt).toLocaleString()}</td>
                    <td>{h.changedBy}</td>
                    <td>{(h.basisPoints / 100).toFixed(2)}%</td>
                    <td>{(h.minFeeCents / 100).toFixed(2)} EUR</td>
                    <td>{h.maxFeeCents === null ? '—' : `${(h.maxFeeCents / 100).toFixed(2)} EUR`}</td>
                    <td>{h.enabled ? '✓' : '✗'}</td>
                    <td className="reason-col">{h.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <EditConfigModal
          current={config}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSuccess('Config updated and history row written');
            void load();
          }}
        />
      )}
    </div>
  );
};

const EditConfigModal: React.FC<{ current: Config; onClose: () => void; onSaved: () => void }> = ({
  current,
  onClose,
  onSaved,
}) => {
  const [basisPoints, setBasisPoints] = useState<number>(current.basisPoints);
  const [minFeeCents, setMinFeeCents] = useState<number>(current.minFeeCents);
  const [maxCapEnabled, setMaxCapEnabled] = useState<boolean>(current.maxFeeCents !== null);
  const [maxFeeCents, setMaxFeeCents] = useState<number>(current.maxFeeCents ?? 0);
  const [enabled, setEnabled] = useState<boolean>(current.enabled);
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reasonValid = reason.trim().length >= 10;

  const submit = async () => {
    setErr(null);
    if (!reasonValid) { setErr('Reason is required, min 10 chars'); return; }
    setSubmitting(true);
    try {
      await apiService.updatePlatformFeeConfig({
        basisPoints,
        minFeeCents,
        maxFeeCents: maxCapEnabled ? maxFeeCents : null,
        enabled,
        reason,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Edit platform fee config</h2>
        <p className="modal-sub">Changes audit-trail to <code>platform_fee_config_history</code> (UKGC retention — never purged).</p>
        {err && <div className="alert alert-error">{err}</div>}
        <div className="form-grid">
          <label>
            <span>Basis points (200 = 2.00%)</span>
            <input type="number" min={0} max={10000} value={basisPoints} onChange={(e) => setBasisPoints(Number(e.target.value))} />
          </label>
          <label>
            <span>Min fee (cents)</span>
            <input type="number" min={0} value={minFeeCents} onChange={(e) => setMinFeeCents(Number(e.target.value))} />
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={maxCapEnabled} onChange={(e) => setMaxCapEnabled(e.target.checked)} />
            <span>Cap maximum fee</span>
          </label>
          <label>
            <span>Max fee (cents, only if cap on)</span>
            <input type="number" min={0} value={maxFeeCents} onChange={(e) => setMaxFeeCents(Number(e.target.value))} disabled={!maxCapEnabled} />
          </label>
          <label className="checkbox-row" style={{ gridColumn: '1 / span 2' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>Fee enabled (uncheck to disable charging entirely — bets pay 0 fee)</span>
          </label>
          <label className="checkbox-row" style={{ flexDirection: 'column', alignItems: 'stretch', gridColumn: '1 / span 2' }}>
            <span>Reason for this change (≥ 10 chars, audit trail)</span>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this change? UKGC will ask."
              className="reason-input"
            />
          </label>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting || !reasonValid}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformFeeConfigPage;
